import type {
  InfiniteData,
  UseInfiniteQueryReturnType,
  UseQueryReturnType,
} from "@tanstack/vue-query";
import { computed, type MaybeRefOrGetter, type Ref, toValue } from "vue";

import { uniqueBy } from "@/lib/collections";

import type { GenericModel } from "./model";

const noop = () => {};

export interface NormalizedDataSource<T extends GenericModel> {
  readonly data: T[];
  readonly count: number | undefined;

  readonly error: unknown;

  /** first-time load, no data yet */
  readonly isLoading: boolean;
  readonly isLoadingError: boolean;

  /** first-time load, but data is present */
  readonly isRefetching: boolean;
  readonly isRefetchError: boolean;

  /** fetching the next page */
  readonly isFetchingNextPage: boolean;
  readonly isFetchNextPageError: boolean;
  readonly hasNextPage: boolean;

  readonly refetch: () => void;
  readonly fetchNextPage: () => void;
}

interface QueryDataSource<T extends GenericModel> {
  query: UseQueryReturnType<T[], any>;
  filter?: (item: T) => unknown;
}

const fromQueryDataSource = <T extends GenericModel>(
  source: QueryDataSource<T> & { type?: "query" }
): NormalizedDataSource<T> => {
  const query = source.query;
  const filter = source.filter;

  const data = computed(() => {
    let array = query.data.value ?? [];
    if (filter) {
      array = array.filter(filter);
    }

    return array;
  });

  return {
    get data() {
      return data.value;
    },
    count: undefined,

    get error() {
      return query.error.value;
    },

    get isLoading() {
      return query.isLoading.value;
    },
    get isLoadingError() {
      return query.isLoadingError.value;
    },

    get isRefetching() {
      return query.isRefetching.value;
    },
    get isRefetchError() {
      return query.isRefetchError.value;
    },

    isFetchingNextPage: false,
    isFetchNextPageError: false,
    hasNextPage: false,

    fetchNextPage: noop,
    refetch: query.refetch,
  };
};

interface InfiniteQueryDataSource<
  T extends GenericModel,
  O extends GenericModel = T
> {
  query: UseInfiniteQueryReturnType<InfiniteData<{ results: T[] }>, any> & {
    flattenedData?: Ref<T[]>;
    count?: Ref<number | undefined>;
  };
  select?: (data: T[]) => O[];
}

export const fromInfiniteQueryDataSource = <
  T extends GenericModel,
  O extends GenericModel = T
>(
  source: InfiniteQueryDataSource<T, O>
): NormalizedDataSource<O> => {
  const query = source.query;

  const count = query.count;
  let flattenedData = query.flattenedData;

  // if it's not from `useGenericListingQuery`, it won't have `flattenedData`,
  // just create one here.
  if (!flattenedData) {
    const data = query.data;

    flattenedData = computed((): T[] => {
      const result = data.value;
      if (!result) {
        return [];
      }

      return uniqueBy(
        result.pages.flatMap((page) => page.results),
        (item) => item.id
      );
    });
  }

  let mappedData = flattenedData as unknown as Ref<O[]>;
  if (source.select) {
    const selectFn = source.select;

    mappedData = computed((): O[] => selectFn(flattenedData.value));
  }

  return {
    get data() {
      return mappedData.value;
    },
    get count() {
      return count?.value;
    },

    get error() {
      return query.error.value;
    },

    get isLoading() {
      return query.isLoading.value;
    },
    get isLoadingError() {
      return query.isLoadingError.value;
    },

    get isRefetching() {
      return query.isRefetching.value;
    },
    get isRefetchError() {
      return query.isRefetchError.value;
    },

    get isFetchingNextPage() {
      return query.isFetchingNextPage.value;
    },
    get isFetchNextPageError() {
      return query.isFetchNextPageError.value;
    },
    get hasNextPage() {
      return query.hasNextPage.value;
    },

    refetch: query.refetch,
    fetchNextPage: query.fetchNextPage,
  };
};

interface ManualDataSource<T extends GenericModel> {
  data: MaybeRefOrGetter<T[] | undefined>;
  count?: MaybeRefOrGetter<number | undefined>;
  error?: MaybeRefOrGetter<unknown>;

  isLoading?: MaybeRefOrGetter<boolean>;
  isLoadingError?: MaybeRefOrGetter<boolean>;

  isRefetching?: MaybeRefOrGetter<boolean>;
  isRefetchError?: MaybeRefOrGetter<boolean>;

  isFetchingNextPage?: MaybeRefOrGetter<boolean>;
  isFetchNextPageError?: MaybeRefOrGetter<boolean>;
  hasNextPage?: MaybeRefOrGetter<boolean>;

  refetch?: () => void;
  fetchNextPage?: () => void;
}

export const fromManualDataSource = <T extends GenericModel>(
  source: ManualDataSource<T>
): NormalizedDataSource<T> => {
  const data = computed((): T[] => {
    return toValue(source.data) ?? [];
  });

  return {
    get data() {
      return data.value;
    },
    get count() {
      return toValue(source.count);
    },

    get error() {
      return toValue(source.error);
    },

    get isLoading() {
      return toValue(source.isLoading) ?? false;
    },
    get isLoadingError() {
      return toValue(source.isLoadingError) ?? false;
    },

    get isRefetching() {
      return toValue(source.isRefetching) ?? false;
    },
    get isRefetchError() {
      return toValue(source.isRefetchError) ?? false;
    },

    get isFetchingNextPage() {
      return toValue(source.isFetchingNextPage) ?? false;
    },
    get isFetchNextPageError() {
      return toValue(source.isFetchNextPageError) ?? false;
    },
    get hasNextPage() {
      return toValue(source.hasNextPage) ?? false;
    },

    refetch: source.refetch ?? noop,
    fetchNextPage: source.fetchNextPage ?? noop,
  };
};

export type DataSource<T extends GenericModel, O extends GenericModel = T> =
  | (ManualDataSource<T> & { type?: "manual" })
  | (QueryDataSource<T> & { type: "query" })
  | (InfiniteQueryDataSource<T, O> & { type: "infinite-query" });

export const normalizeDataSource = <
  T extends GenericModel,
  O extends GenericModel = T
>(
  source: DataSource<O>
): NormalizedDataSource<O> => {
  switch (source.type) {
    case "infinite-query": {
      return fromInfiniteQueryDataSource(source);
    }
    case "query": {
      return fromQueryDataSource(source);
    }
    case "manual":
    case undefined: {
      return fromManualDataSource(source);
    }
  }
};
